/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { request } from '@/api/client';

export const counterpartySchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(['person', 'organization']),
  email: z.string().nullable(),
  agreement_count: z.number(),
  /** What the In progress tab lists for them, and what Completed lists (ADW-5503). */
  in_progress_count: z.number(),
  completed_count: z.number(),
});

export type Counterparty = z.infer<typeof counterpartySchema>;

/** A page of counterparties. Never the whole list, which only grows (`DB-6`). */
export const counterpartyPageSchema = z.object({
  items: z.array(counterpartySchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  has_more: z.boolean(),
});

export type CounterpartyPage = z.infer<typeof counterpartyPageSchema>;

export const COUNTERPARTY_SORTS = ['name', 'email', 'kind', 'agreements'] as const;
export type CounterpartySort = (typeof COUNTERPARTY_SORTS)[number];

/** The most one page holds, as the server allows. */
export const MOST_PER_PAGE = 100;

export interface CounterpartyFilters {
  /** Part of a name or email. */
  search?: string | undefined;
  sort?: CounterpartySort | undefined;
  direction?: 'asc' | 'desc' | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

/** The query string for a set of filters: only what is set is sent. */
export function counterpartyParams(filters: CounterpartyFilters): URLSearchParams {
  const params = new URLSearchParams({ limit: String(filters.limit ?? 25) });
  const search = filters.search?.trim();
  if (search) params.set('q', search);
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.direction) params.set('direction', filters.direction);
  if (filters.offset) params.set('offset', String(filters.offset));
  return params;
}

export const counterpartiesQuery = (filters: CounterpartyFilters = {}) =>
  queryOptions({
    queryKey: ['counterparties', filters] as const,
    queryFn: ({ signal }) =>
      request(
        `/api/v1/counterparties?${counterpartyParams(filters).toString()}`,
        counterpartyPageSchema,
        { signal },
      ),
  });

/**
 * What a picker offers: the first page, by name, as large as a page may be.
 * A picker is a convenience over a typed name, so a long list being cut is a
 * shorter menu, never a failure.
 */
export const counterpartyChoicesQuery = () =>
  counterpartiesQuery({ sort: 'name', direction: 'asc', limit: MOST_PER_PAGE });

/**
 * Merging counterparties that are one (`CQ-09`). Names are compared ignoring
 * capitals, spacing and punctuation at the end, and rows made before that
 * rule may share a name: those are listed as likely duplicates. An
 * administrator may also merge any counterparties of one kind, whatever their
 * names. They keep one and merge the rest into it; the server decides who may.
 */
export const duplicateMemberSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(['person', 'organization']),
  email: z.string().nullable(),
  is_active: z.boolean(),
  created_at: z.string(),
  agreement_count: z.number(),
});

export type DuplicateMember = z.infer<typeof duplicateMemberSchema>;

export const duplicateGroupSchema = z.object({
  name_key: z.string(),
  members: z.array(duplicateMemberSchema),
});

export type DuplicateGroup = z.infer<typeof duplicateGroupSchema>;

/** A page of groups. Never the whole of it (`DB-6`). */
export const duplicatePageSchema = z.object({
  items: z.array(duplicateGroupSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  has_more: z.boolean(),
});

/** Groups shown on one page of the merge screen. */
export const DUPLICATES_PER_PAGE = 25;

export const duplicatesQuery = (offset = 0) =>
  queryOptions({
    queryKey: ['counterparty-duplicates', offset] as const,
    queryFn: ({ signal }) =>
      request(
        `/api/v1/counterparties/duplicates?limit=${String(DUPLICATES_PER_PAGE)}&offset=${String(offset)}`,
        duplicatePageSchema,
        { signal },
      ),
  });

export const mergedSchema = z.object({
  kept_id: z.string(),
  merged_ids: z.array(z.string()),
  agreements_moved: z.number(),
});

export type Merged = z.infer<typeof mergedSchema>;

/** The most one merge may fold into the one kept, as the server allows. */
export const MOST_MERGED = 50;

/** Move every agreement of `mergeIds` to `keepId`, and switch those off. */
export const mergeCounterparties = (keepId: string, mergeIds: string[]) =>
  request('/api/v1/counterparties/merge', mergedSchema, {
    method: 'POST',
    body: { keep_id: keepId, merge_ids: mergeIds },
  });
